<template>
	<section class="page">
		<div class="content">
			<span class="logo_placeholder" ref="placeholder">
				<IconLogoBig ref="logo" />
			</span>
		</div>
		<span class="title" v-if="title">{{ title }}</span>

		<div class="content">
			<ul class="news-list">
				<li v-for="(news, i) in news" :key="`news_${i}`">
					<span class="date">{{ news.date }}</span>
					<h2>{{ news.title }}</h2>
					<div class="excerpt" v-html="news.excerpt"></div>
					<router-link v-if="news.content" :to="news.permalink | clearUrl">
						<span @mouseenter="onPointerMouseEnter($event, 'text')" @mouseleave="onPointerMouseLeave($event, 'text')">{{
							news.more
						}}</span>
					</router-link>
				</li>
			</ul>
			<button
				type="button"
				@click="loadMoreNews"
				v-show="existMoreNews"
				@mouseenter="onPointerMouseEnter($event)"
				@mouseleave="onPointerMouseLeave($event)"
				class="all-news"
			>
				{{ loadMore }}
			</button>
		</div>
	</section>
</template>

<script>
import { mapState } from "vuex";
import { NewsService } from "@/api";
import IconLogoBig from "@/components/icons/IconLogoBig";
import pointerHove from "@/mixins/pointerHover";

export default {
	name: "AllNewsView",
	mixins: [pointerHove],
	props: {
		slug: String,
	},
	components: {
		IconLogoBig,
	},
	computed: {
		...mapState({
			currentLanguage: (state) => state.site.currentLanguage,
			strings: (state) => state.site.strings,
		}),
		loadMore() {
			return this.strings && this.strings.loadMore && this.strings.loadMore[this.currentLanguage];
		},
		existMoreNews() {
			return this.news.length < this.allNewsCounter;
		},
	},
	data() {
		return {
			news: [],
			title: "All News",
			offset: 0,
			amount: 6,
			allNewsCounter: 0,
		};
	},
	metaInfo() {
		return {
			title: this.title,
		};
	},
	created() {
		this.fetchData();
	},
	methods: {
		async fetchData() {
			const resp = await NewsService.getNews(this.currentLanguage, this.amount, this.offset);
			this.news = resp?.news;
			this.title = resp?.title;
			this.allNewsCounter = resp?.allCounter;
		},
		async loadMoreNews() {
			this.offset += this.amount;
			const resp = await NewsService.getNews(this.currentLanguage, this.amount, this.offset);
			if (resp?.news && resp.news.length > 0) {
				this.news.push(...resp.news);
			} else {
				this.existMoreNews = false;
			}
		},
	},
	watch: {
		async currentLanguage(newValue) {
			this.offset = 0;
			const resp = await NewsService.getNews(newValue, this.amount, this.offset);
			this.news = resp?.news;
			this.title = resp?.title;
		},
	},
};
</script>

<style lang="scss" scoped>
.page {
	span.title {
		@include gridColumn(s, 12, 0, false, true);
		@include fontH0;
		display: block;

		@include mq($from: xl) {
			@include gridColumn(xl, 14, 1, false, true);
		}
	}

	.logo_placeholder {
		width: 100%;
		opacity: 1;
		display: none;

		@include mq($from: m) {
			@include gridColumn(m, 14, 1, false, true);
			display: block;
			margin-bottom: 75px;
			margin-top: -2vw;
		}

		@include mq($from: l) {
			margin-top: -2.5vw;
		}

		@include mq($from: xl) {
			margin-bottom: 120px;
			@include gridColumn(xl, 10, 2, false, true);
		}

		@include mq($from: 2500px) {
			margin-top: -62px;
		}

		svg {
			width: 100%;
			height: auto;
		}
	}

	button.all-news {
		display: block;
		text-align: left;
		background: $colorRed1;
		border-radius: 33% 40% 35% 45% / 60% 60% 60% 60%;
		padding: 5px 15px;
		margin-top: 36px;
		margin-left: -15px;
		border: none;
		outline: none;
		font-weight: 500;
		font-size: 18px;
		font-family: $fontRubik;
		text-decoration: underline;
		margin: 60px auto;
		cursor: pointer;
	}
}

.news-list {
	list-style: none;
	margin: 0;
	padding: 0;
	text-align: left;
	display: inline-block;
	max-width: 100%;
	width: 100%;
	display: flex;
	justify-content: flex-start;
	flex-wrap: wrap;

	@include mq($from: xl) {
		@include gridColumn(xl, 22, 1, false, true);
	}

	li {
		margin-top: 50px;
		flex: 0 0 100%;

		@include mq($from: m) {
			flex: 0 0 46%;
		}

		@include mq($from: l) {
			flex: 0 0 30%;
			margin-right: 5%;

			&:nth-of-type(3n + 3) {
				margin-right: 0;
			}
		}
	}

	span.date {
		@include fontCopyS;
		display: inline-block;
		margin-bottom: 10px;
	}

	h2 {
		@include fontCopy;
		font-weight: 500;
	}

	.excerpt {
		@include fontCopy;
		margin-top: 10px;

		&::v-deep p {
			@include fontCopy;

			a {
				transition: color 0.3s;

				&:hover {
					color: $colorRed1 !important;
				}
			}
		}
	}

	a {
		font-weight: 500;
		margin-top: 10px;
		font-size: 18px;
		display: inline-block;
	}
}
</style>
